import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cx, Skeleton } from "./Controls";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  className?: string;
  hideOnMobile?: boolean;
}

export function Table<T extends { id: string }>({ columns, rows, loading, empty, onRowClick, rowClassName, page, total, limit, onPage, testId }: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  empty?: React.ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  page?: number;
  total?: number;
  limit?: number;
  onPage?: (p: number) => void;
  testId?: string;
}) {
  const id = testId || "table";
  if (loading && !rows.length) {
    return (
      <div className="space-y-2" data-testid={`${id}-loading`}>
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (!rows.length) return <>{empty}</>;
  const pages = total && limit ? Math.max(1, Math.ceil(total / limit)) : 1;
  return (
    <div data-testid={id}>
      <div className="hidden overflow-x-auto rounded-xl border border-line bg-white md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-slate-50 text-xs text-slate-500">
            <tr>{columns.map((c) => <th key={c.key} className={cx("px-4 py-3 font-semibold", c.className)}>{c.header}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} onClick={() => onRowClick?.(r)} className={cx(onRowClick && "cursor-pointer hover:bg-slate-50", "transition-colors", rowClassName?.(r))} data-testid={`${id}-row`}>
                {columns.map((c) => <td key={c.key} className={cx("px-4 py-3 align-middle", c.className)}>{c.render(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-2 md:hidden">
        {rows.map((r) => (
          <div key={r.id} onClick={() => onRowClick?.(r)} className={cx("rounded-xl border border-line bg-white p-4", onRowClick && "cursor-pointer", rowClassName?.(r))} data-testid={`${id}-card`}>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
              {columns.filter((c) => !c.hideOnMobile).map((c) => (
                <React.Fragment key={c.key}>
                  <dt className="text-xs text-slate-500">{c.header}</dt>
                  <dd className="min-w-0 truncate text-right">{c.render(r)}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>
      {total !== undefined && (
        <div className="mt-3 flex items-center justify-between text-sm text-slate-500" data-testid={`${id}-pagination`}>
          <span data-testid={`${id}-total`}>{total} en total</span>
          {onPage && pages > 1 && (
            <div className="flex items-center gap-2">
              <button className="rounded-lg border border-line bg-white p-1.5 hover:bg-slate-50 disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${id}-prev-page`} aria-label="Página anterior"><ChevronLeft className="h-4 w-4" /></button>
              <span className="tabular text-sm">{page} / {pages}</span>
              <button className="rounded-lg border border-line bg-white p-1.5 hover:bg-slate-50 disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${id}-next-page`} aria-label="Página siguiente"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
